import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { governanceService } from '@/services/governance';
import type { ProposalInfo } from '@/types/governance';

// Query keys
const governanceKeys = {
  all: ['governance'] as const,
  proposals: () => [...governanceKeys.all, 'proposals'] as const,
  proposalList: (filters?: { active?: boolean; status?: number }) =>
    [...governanceKeys.proposals(), filters] as const,
  proposal: (id: number) => [...governanceKeys.proposals(), id] as const,
};

/**
 * Hook to fetch all proposals with optional filters
 */
export function useProposals(filters?: { active?: boolean; status?: number }): UseQueryResult<ProposalInfo[], Error> {
  return useQuery<ProposalInfo[], Error>({
    queryKey: governanceKeys.proposalList(filters),
    queryFn: () => governanceService.getProposals(filters?.active, filters?.status),
    staleTime: 60_000, // 60 seconds
  });
}

/**
 * Hook to fetch only active proposals (in voting period)
 */
export function useActiveProposals(): UseQueryResult<ProposalInfo[], Error> {
  return useQuery<ProposalInfo[], Error>({
    queryKey: governanceKeys.proposalList({ active: true }),
    queryFn: () => governanceService.getActiveProposals(),
    staleTime: 60_000, // 60 seconds
  });
}

/**
 * Hook to fetch a single proposal by ID
 */
export function useProposal(proposalId: number | undefined): UseQueryResult<ProposalInfo, Error> {
  return useQuery<ProposalInfo, Error>({
    queryKey: governanceKeys.proposal(proposalId ?? 0),
    queryFn: () => governanceService.getProposal(proposalId!),
    enabled: proposalId !== undefined && proposalId > 0,
    staleTime: 60_000, // 60 seconds
  });
}

/**
 * Hook to invalidate governance queries
 */
export function useInvalidateGovernance(): {
  invalidateProposals: () => Promise<void>;
  invalidateProposal: (id: number) => Promise<void>;
  invalidateAll: () => Promise<void>;
  setProposals: (proposals: ProposalInfo[]) => void;
} {
  const queryClient = useQueryClient();

  return {
    invalidateProposals: (): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: governanceKeys.proposals() }),
    invalidateProposal: (id: number): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: governanceKeys.proposal(id) }),
    invalidateAll: (): Promise<void> =>
      queryClient.invalidateQueries({ queryKey: governanceKeys.all }),
    // Update proposals data directly
    setProposals: (proposals: ProposalInfo[]): void => {
      queryClient.setQueryData(governanceKeys.proposalList(undefined), proposals);
    },
  };
}
